import { FlatTreeControl } from '@angular/cdk/tree';
import { Component, Inject, OnInit } from '@angular/core';
import { UntypedFormControl, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import {
  MatTreeFlatDataSource,
  MatTreeFlattener
} from '@angular/material/tree';
import { Router } from '@angular/router';
import {
  validarAdicaoRaiz,
  verificarRotasDeAcessoRaiz
} from '@app/shared/utils';
import {
  CustomValidators,
  EntityService,
  TokenService
} from 'viggo-sistemas-core-web';

export interface NodeConfirmDialog {
  descricao: string;
  msg_erro: string;
  mensagem: string;
  link?: string;
  url_validate?: any;
  mensagens?: NodeConfirmDialog[];
}

export interface FlatNodeConfirmDialog {
  expandable: boolean;
  descricao: string;
  msg_erro: string;
  mensagem: string;
  link?: string;
  url_validate?: any;
  level: number;
}
export interface ConfirmDialogData {
  title: string;
  bodyMessage: string;
  labelCloseButton?: string;
  labelConfirmButton?: string;
  colorCloseButton?: string;
  colorConfirmButton?: string;
  optionCheckBox?: boolean;
  labelOptionCheckBox?: string;
  existsObs?: boolean;
  obsValue?: string;
  singleBtn?: boolean;
  breakline?: boolean;
  maxwidth?: string;
  icon?: string;
  options_select_label?: string;
  options?: [
    {
      label: string;
      value: any;
      disabled: boolean;
    }
  ];
  options_select?: [
    {
      label: string;
      value: any;
    }
  ];
  useCheckbox?: boolean;
  multipleMsgs?: boolean;
  submsgs?: any[];
  subBodyMessage?: string;
  showCloseBtn?: boolean; // exibe o botao de fechar dialog
  useAccessRouterSubMsg?: boolean; // CASO VENHA TRUE O SUBMSGS VAI CONTER UM LINK DE ACESSO
  subMsgsTree?: NodeConfirmDialog[];
  showMultipleMsg?: boolean;
  usaFastAutocomplete?: boolean;
  configFastAutocomplete?: {
    class: string;
    placeholder: string;
    entityService: EntityService<any>;
    displayField: string;
    pageSizeValue: string;
    pageValue: string;
    domain_id: string;
    actionState: boolean;
    displayWith?: (obj: any) => string;
    include?: string[];
    value_order?: string;
    orderByPath?: string;
    displaySecondField?: string;
    searchField?: string;
    searchFieldPath?: string;
    domain_path?: string;
    pagePath?: string;
    pageSizePath?: string;
    messages?: {};
    insertType?: string;
  };
}

@Component({
  selector: 'confirm-dialog',
  templateUrl: './confirm-dialog.component.html'
})
export class ConfirmDialogComponent implements OnInit {
  public observacaoFormControl: UntypedFormControl;
  public estoqueFormControl: UntypedFormControl;
  public entityAutoCompleteControl: UntypedFormControl;

  maxwidth = 'max-width: auto';
  selectedValue: string;
  selectedValueOption: string;
  checkboxValues = [];

  private _transformer = (node: NodeConfirmDialog, level: number) => {
    return {
      expandable: !!node.mensagens && node.mensagens.length > 0,
      descricao: node.descricao,
      msg_erro: node.msg_erro,
      mensagem: node.mensagem,
      url_validate: node.url_validate,
      link: node.link,
      level: level
    };
  };
  treeControl = new FlatTreeControl<FlatNodeConfirmDialog>(
    node => node.level,
    node => node.expandable
  );

  treeFlattener = new MatTreeFlattener(
    this._transformer,
    node => node.level,
    node => node.expandable,
    node => node.mensagens
  );

  dataSource = new MatTreeFlatDataSource(this.treeControl, this.treeFlattener);

  // eslint-disable-next-line no-useless-constructor
  constructor(
    protected router: Router,
    protected tokenService: TokenService,
    public matDialogRef: MatDialogRef<ConfirmDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: ConfirmDialogData
  ) {
    if (data.showMultipleMsg) {
      this.dataSource.data = data.subMsgsTree;
    }
  }

  /**
   * @lifeCycle
   */
  public ngOnInit(): void {
    this.observacaoFormControl = new UntypedFormControl(
      this.data.obsValue != null ? this.data.obsValue : '',
      [Validators.maxLength(500), Validators.required]
    );

    this.entityAutoCompleteControl = new UntypedFormControl(null, [
      CustomValidators.typeIs('object'),
      CustomValidators.isEmptyValue()
    ]);

    if (this.data.usaFastAutocomplete) {
      this.entityAutoCompleteControl.addValidators([Validators.required]);
    }

    this.estoqueFormControl = new UntypedFormControl(false);
    this.estoqueFormControl.markAsDirty();
    this.observacaoFormControl.markAsDirty();
    this.entityAutoCompleteControl.markAsDirty();
    this.entityAutoCompleteControl.markAsTouched();
  }

  /**
   * Salva as informacoes inseridas repassando para o component que chamou o dialog
   */
  public confirmar(): void {
    if (
      !this.data.existsObs &&
      !this.data.options &&
      !this.data.options_select &&
      !this.data.usaFastAutocomplete
    )
      this.matDialogRef.close(true);
    else {
      if (this.data.options && !this.data.options_select) {
        this.matDialogRef.close({
          confirm: true,
          option: this.data.useCheckbox
            ? this.checkboxValues
            : this.selectedValue
        });
      }
      if (!this.data.options && this.data.options_select) {
        this.matDialogRef.close({
          confirm: true,
          option: this.selectedValueOption
        });
      }
      if (this.data.options && this.data.options_select) {
        this.matDialogRef.close({
          confirm: true,
          option: this.data.useCheckbox
            ? this.checkboxValues
            : this.selectedValue,
          optionSelect: this.selectedValueOption
        });
      }
      if (
        this.observacaoFormControl.invalid ||
        this.estoqueFormControl.invalid ||
        this.entityAutoCompleteControl.invalid
      ) {
        this.observacaoFormControl.updateValueAndValidity();
        this.estoqueFormControl.updateValueAndValidity();
        this.entityAutoCompleteControl.updateValueAndValidity();
        return;
      }

      const estoque = this.estoqueFormControl.value;
      const observacao = this.observacaoFormControl.value;
      const entity_id = this.entityAutoCompleteControl.value?.id;
      this.matDialogRef.close({
        confirm: true,
        observacao,
        estoque,
        entity_id
      });
    }
  }

  /**
   * Preenche as seleções de checkbox para serem emitidas para o componente pai
   * @param value
   */
  selectCheckBox(value) {
    if (this.checkboxValues.length !== 0) {
      const index = this.checkboxValues.findIndex(
        select_key => Object.keys(select_key)[0] === Object.keys(value)[0]
      );
      if (index >= 0) {
        this.checkboxValues.splice(index, 1);
      } else this.checkboxValues.push(value);
    } else this.checkboxValues.push(value);
  }

  /**
   * Envia o usuário para uma rota especifica do projeto
   * @param route
   */
  sendTo(route) {
    const url = this.router.serializeUrl(this.router.createUrlTree([route]));
    window.open(url, '_blank');
  }

  hasChild = (_: number, node: FlatNodeConfirmDialog) => node.expandable;

  verificarDisponibilidade(url: { method: string; url: string }) {
    const routesAcess = verificarRotasDeAcessoRaiz(
      this.tokenService.getRoutes(),
      url.url
    );
    return validarAdicaoRaiz(routesAcess, url.method, '', url.url);
  }
}
